import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, ArrowLeft, Coins, Mail, Ticket, Crown, Trophy, Check, Plus, Trash2, RefreshCw, Image as ImageIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { API } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import RateChart from "../components/RateChart";

const TABS = [
  { key: "overview", label: "Overview", icon: Coins },
  { key: "rates", label: "Live Rates", icon: Coins },
  { key: "enquiries", label: "Enquiries", icon: Mail },
  { key: "lucky", label: "Lucky Draw", icon: Ticket },
  { key: "signups", label: "Scheme Signups", icon: Crown },
  { key: "winners", label: "Winners", icon: Trophy },
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [rates, setRates] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [lucky, setLucky] = useState([]);
  const [signups, setSignups] = useState([]);
  const [winners, setWinners] = useState([]);
  const [newWinner, setNewWinner] = useState({ month: "AUGUST", year: 2026, rank: "coin", name: "", prize: "1 GRAM SILVER", cat: "Pure Silver" });
  const [history, setHistory] = useState([]);

  const loadAll = async () => {
    try {
      const [s, r, e, l, sg, w, h] = await Promise.all([
        api.get("/admin/stats"), api.get("/rates"), api.get("/admin/enquiries"),
        api.get("/admin/lucky-draw"), api.get("/admin/scheme-signups"), api.get("/winners"),
        api.get("/rates/history?days=14"),
      ]);
      setStats(s.data); setRates(r.data); setEnquiries(e.data);
      setLucky(l.data); setSignups(sg.data); setWinners(w.data); setHistory(h.data);
    } catch (err) {
      toast({ title: "Failed to load", description: "Check your admin access." });
    }
  };

  useEffect(() => { loadAll(); }, []);

  const saveRates = async () => {
    try {
      const payload = { ...rates };
      delete payload._id;
      await api.put("/admin/rates", payload);
      toast({ title: "Rates updated", description: "Live rates are now reflected across the site." });
      api.get("/rates/history?days=14").then((h) => setHistory(h.data)).catch(() => {});
    } catch (e) { toast({ title: "Update failed" }); }
  };

  const confirmSignup = async (id) => {
    await api.patch(`/admin/scheme-signups/${id}`, { status: "confirmed" });
    setSignups((p) => p.map((s) => (s.id === id ? { ...s, status: "confirmed" } : s)));
    loadAll();
  };

  const markEnquiry = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status });
    setEnquiries((p) => p.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  const addWinner = async (e) => {
    e.preventDefault();
    if (!newWinner.name) { toast({ title: "Winner name/number required" }); return; }
    const res = await api.post("/admin/winners", { ...newWinner, year: parseInt(newWinner.year, 10) });
    setWinners((p) => [res.data, ...p]);
    setNewWinner({ ...newWinner, name: "" });
    toast({ title: "Winner added" });
  };

  const delWinner = async (id) => {
    await api.delete(`/admin/winners/${id}`);
    setWinners((p) => p.filter((w) => w.id !== id));
  };

  return (
    <div className="min-h-screen bg-ink grid-bg">
      <div className="max-w-[1200px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display smallcaps text-4xl text-white">Admin Dashboard</h1>
            <p className="font-body text-gray-400">TwinBrain Jewellery · {user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadAll} className="flex items-center gap-2 border border-white/15 text-gray-300 font-ui text-[11px] tracking-[0.2em] uppercase px-4 py-2.5 hover:border-gold hover:text-gold transition-colors"><RefreshCw size={14} /> Refresh</button>
            <Link to="/" className="flex items-center gap-2 font-ui text-[11px] tracking-[0.2em] uppercase text-gray-400 hover:text-gold transition-colors"><ArrowLeft size={14} /> Site</Link>
            <button onClick={logout} className="flex items-center gap-2 border border-white/15 text-gray-300 font-ui text-[11px] tracking-[0.2em] uppercase px-4 py-2.5 hover:border-gold hover:text-gold transition-colors"><LogOut size={14} /> Logout</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mt-8 border-b border-white/8 pb-3">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 font-ui text-[11px] tracking-[0.2em] uppercase px-4 py-2.5 transition-colors ${tab === t.key ? "gold-btn" : "text-gray-300 hover:text-gold border border-white/10"}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "overview" && stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {[
                { l: "Enquiries", v: stats.enquiries },
                { l: "Lucky Draw Entries", v: stats.lucky_draw },
                { l: "Scheme Signups", v: stats.scheme_signups },
                { l: "Signups Pending", v: stats.signups_pending },
                { l: "Winners Listed", v: stats.winners },
                { l: "Customers", v: stats.customers },
              ].map((c, i) => (
                <div key={i} className="border border-white/8 bg-white/[0.02] p-8 card-glow">
                  <div className="font-display text-5xl gold-gradient-text">{c.v}</div>
                  <div className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-2">{c.l}</div>
                </div>
              ))}
            </div>
          )}

          {tab === "rates" && rates && (
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="border border-white/10 bg-white/[0.02] p-8">
              <h2 className="font-display text-2xl text-white mb-6">Update Live Rates</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Gold 24K (₹ / 10g)"><input type="number" className="ad-input" value={rates.gold24k.value} onChange={(e) => setRates({ ...rates, gold24k: { ...rates.gold24k, value: parseInt(e.target.value || 0, 10) } })} /></Field>
                <Field label="Silver (₹ / kg)"><input type="number" className="ad-input" value={rates.silver.value} onChange={(e) => setRates({ ...rates, silver: { ...rates.silver, value: parseInt(e.target.value || 0, 10) } })} /></Field>
                <Field label="Updated label"><input className="ad-input" value={rates.updated} onChange={(e) => setRates({ ...rates, updated: e.target.value })} /></Field>
                <Field label="City"><input className="ad-input" value={rates.city} onChange={(e) => setRates({ ...rates, city: e.target.value })} /></Field>
              </div>
              <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-6 mb-3">Small Coin Prices</p>
              <div className="grid sm:grid-cols-3 gap-4">
                {rates.small_rates.map((sr, i) => (
                  <div key={i} className="border border-white/8 p-3">
                    <p className="font-display text-gold">{sr.label} {sr.name}</p>
                    <input className="ad-input mt-2" value={sr.price} onChange={(e) => {
                      const arr = [...rates.small_rates]; arr[i] = { ...sr, price: e.target.value }; setRates({ ...rates, small_rates: arr });
                    }} />
                  </div>
                ))}
              </div>
              <button onClick={saveRates} className="gold-btn font-ui text-xs tracking-[0.2em] uppercase px-8 py-4 mt-8 font-medium">Save Rates</button>
              <p className="font-body text-gray-500 text-xs mt-3">Saving records today's snapshot for the price trend chart.</p>
              </div>

              <div className="border border-white/10 bg-white/[0.02] p-8">
                <h2 className="font-display text-2xl text-white mb-6">Price Trend</h2>
                <RateChart data={history} />
              </div>
            </div>
          )}

          {tab === "enquiries" && (
            <Table cols={["Name", "Phone", "Interest", "Message", "Status", ""]}>
              {enquiries.map((e) => (
                <tr key={e.id} className="border-b border-white/5 hover:bg-gold/[0.03]">
                  <Td>{e.name}</Td><Td>{e.phone}</Td><Td>{e.interest || "—"}</Td><Td className="max-w-[220px] truncate">{e.message || "—"}</Td>
                  <Td>{e.status === "done" ? <Badge gold>Done</Badge> : <Badge>New</Badge>}</Td>
                  <Td>{e.status !== "done" && <button onClick={() => markEnquiry(e.id, "done")} className="text-gold hover:underline font-ui text-[10px] tracking-wider uppercase flex items-center gap-1"><Check size={13} /> Done</button>}</Td>
                </tr>
              ))}
            </Table>
          )}

          {tab === "lucky" && (
            <Table cols={["Name", "Mobile", "City", "Number", "Interest"]}>
              {lucky.map((l) => (
                <tr key={l.id} className="border-b border-white/5 hover:bg-gold/[0.03]">
                  <Td>{l.name}</Td><Td>{l.mobile}</Td><Td>{l.city || "—"}</Td><Td className="text-gold">{l.lucky_number ?? "—"}</Td><Td>{l.interest || "—"}</Td>
                </tr>
              ))}
            </Table>
          )}

          {tab === "signups" && (
            <Table cols={["Name", "Phone", "City", "Amount", "Ref", "Proof", "Status", ""]}>
              {signups.map((s) => (
                <tr key={s.id} className="border-b border-white/5 hover:bg-gold/[0.03]">
                  <Td>{s.name}</Td><Td>{s.phone}</Td><Td>{s.city || "—"}</Td><Td className="text-gold">₹{s.amount?.toLocaleString("en-IN")}</Td><Td>{s.payment_ref || "—"}</Td>
                  <Td>{s.screenshot_id
                    ? <a href={`${API}/files/${s.screenshot_id}`} target="_blank" rel="noreferrer" className="text-gold hover:underline font-ui text-[10px] tracking-wider uppercase flex items-center gap-1"><ImageIcon size={13} /> View</a>
                    : <span className="text-gray-600">—</span>}</Td>
                  <Td>{s.status === "confirmed" ? <Badge gold>Confirmed</Badge> : <Badge>Pending</Badge>}</Td>
                  <Td>{s.status !== "confirmed" && <button onClick={() => confirmSignup(s.id)} className="text-gold hover:underline font-ui text-[10px] tracking-wider uppercase flex items-center gap-1"><Check size={13} /> Confirm</button>}</Td>
                </tr>
              ))}
            </Table>
          )}

          {tab === "winners" && (
            <div>
              <form onSubmit={addWinner} className="border border-white/10 bg-white/[0.02] p-6 mb-6 grid sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
                <Field label="Month"><input className="ad-input" value={newWinner.month} onChange={(e) => setNewWinner({ ...newWinner, month: e.target.value })} /></Field>
                <Field label="Year"><input type="number" className="ad-input" value={newWinner.year} onChange={(e) => setNewWinner({ ...newWinner, year: e.target.value })} /></Field>
                <Field label="Rank">
                  <Select value={newWinner.rank} onValueChange={(v) => setNewWinner({ ...newWinner, rank: v })}>
                    <SelectTrigger className="ad-input h-auto"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0b0f19] border-white/10 text-white">
                      <SelectItem value="gold" className="focus:bg-gold/15 focus:text-gold">Gold Medal</SelectItem>
                      <SelectItem value="silvermedal" className="focus:bg-gold/15 focus:text-gold">Silver Medal</SelectItem>
                      <SelectItem value="coin" className="focus:bg-gold/15 focus:text-gold">Coin</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Winner Name/No."><input className="ad-input" value={newWinner.name} onChange={(e) => setNewWinner({ ...newWinner, name: e.target.value })} /></Field>
                <Field label="Prize"><input className="ad-input" value={newWinner.prize} onChange={(e) => setNewWinner({ ...newWinner, prize: e.target.value })} /></Field>
                <button type="submit" className="gold-btn font-ui text-xs tracking-[0.2em] uppercase px-4 py-3 font-medium flex items-center justify-center gap-2"><Plus size={14} /> Add</button>
              </form>
              <Table cols={["Period", "Rank", "Winner", "Prize", "Category", ""]}>
                {winners.map((w) => (
                  <tr key={w.id} className="border-b border-white/5 hover:bg-gold/[0.03]">
                    <Td>{w.month} {w.year}</Td><Td className="capitalize">{w.rank}</Td><Td className="text-white">{w.name}</Td><Td>{w.prize}</Td>
                    <Td>{w.cat === "24K Gold" ? <Badge gold>{w.cat}</Badge> : <Badge>{w.cat}</Badge>}</Td>
                    <Td><button onClick={() => delWinner(w.id)} className="text-destructive hover:underline font-ui text-[10px] tracking-wider uppercase flex items-center gap-1"><Trash2 size={13} /> Delete</button></Td>
                  </tr>
                ))}
              </Table>
            </div>
          )}
        </div>
      </div>
      <style>{`
        .ad-input { width:100%; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.1); padding:0.6rem 0.8rem; color:#fff; font-family:'Poppins',sans-serif; outline:none; transition:border-color .2s; }
        .ad-input:focus { border-color:#d4af37; }
        .ad-input option { background:#0b0f19; }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="font-ui text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const Table = ({ cols, children }) => (
  <div className="border border-white/8 bg-white/[0.02] overflow-x-auto">
    <table className="w-full text-left">
      <thead>
        <tr className="bg-white/[0.03] border-b border-white/8">
          {cols.map((c, i) => <th key={i} className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground px-4 py-3 whitespace-nowrap">{c}</th>)}
        </tr>
      </thead>
      <tbody className="font-body text-gray-300">{children}</tbody>
    </table>
  </div>
);

const Td = ({ children, className = "" }) => <td className={`px-4 py-3.5 align-middle ${className}`}>{children}</td>;

const Badge = ({ children, gold }) => (
  <span className={`font-ui text-[9px] tracking-wider uppercase px-2.5 py-1 ${gold ? "bg-gold/15 text-gold" : "bg-white/8 text-gray-300"}`}>{children}</span>
);

export default AdminDashboard;
